import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import type { TenantConfig, ActionItem } from '../types/tenant';
import { useApiClientStore } from './useApiClientStore';
import { fetchRequestFormStatusApi, fetchTenantConfig } from '../services/tenant.service';
import { RequestTypeShort } from '../types';

export const useTenantStore = defineStore('tenant', () => {
  // ---- State ----
  const apiLoading = ref<boolean>(false);
  const tenant = ref<TenantConfig | null>(null);
  const error = ref<string | null>(null);
  const apiClientStore = useApiClientStore();

  // ---- Getters ----
  const isLoaded = computed(() => !!tenant.value && !apiLoading.value);
  const isNhsJurisdiction = computed(() => tenant.value?.jurisdiction === 'england-nhs');
  const isNhsLoginEnabled = computed(() => tenant.value?.nhsLoginEnabled ?? false);
  const actions = computed<ActionItem[]>(() => tenant.value?.actions ?? []);

  const actionResponseTime = ref<number>();
  const informationBanner = ref<string>();

  const setActionResponseTime = (val) => {
    actionResponseTime.value = actions.value.find((a) =>
      a.href.includes(val),
    )?.estimatedResponseTime;
  };

  const actionDetails = (val) => {
    return actions.value.find((a) => a.href === val);
  };

  const actionOpeningAvailability = (val) => {
    return actions.value.find((a) => a.href.includes(val))?.openingAvailability;
  };

  const setInformationBanner = (requestType: string) => {
    informationBanner.value = actions.value.find((a) =>
      a.href.includes(requestType),
    )?.informationBanner;
  };

  const clearInformationBanner = () => {
    informationBanner.value = null;
  };

  const openActions = computed(() =>
    actions.value.filter((a) => a.status === 'open' || a.status === 'always-open'),
  );
  const pausedActions = computed(() => actions.value.filter((a) => a.status === 'paused'));
  const closedActions = computed(() => actions.value.filter((a) => a.status === 'closed'));
  const inactiveActions = computed(() =>
    actions.value.filter((a) => a.status === 'closed' || a.status === 'paused'),
  );

  // ---- Actions ----
  async function fetchRequestFormStatus(requestType: RequestTypeShort) {
    try {
      const result = await fetchRequestFormStatusApi(requestType);
      if (!result.ok) {
        return {
          ok: false,
          errors: result?.errors,
        };
      }

      return { ok: true, availabilityStatus: result.data.availabilityStatus };
    } catch (e) {
      return { ok: false, errors: e.data?.errors };
    }
  }

  async function fetchTenant(tenantId: string) {
    apiLoading.value = true;
    error.value = null;
    try {
      // --- Case 1: tenantId is missing as marked invalid earlier on ---
      if (!tenantId) {
        error.value = 'Missing tenant ID';
        tenant.value = null;
        console.info('Missing tenant ID');
        apiClientStore.setTenantId(null);
        return;
      }
      if (tenantId === tenant.value?.tenantId) {
        // NB. if same tenant don't reload.
        return;
      }

      apiClientStore.setTenantId(tenantId);

      try {
        const result = await fetchTenantConfig();

        if (!result.tenantConfig.tenantId) {
          error.value = 'no-match';
        } else {
          tenant.value = result.tenantConfig;
          // tenant.value.jurisdiction = 'none';
        }
      } catch (e: any) {
        console.error('[tenant] loadTenant API error', e);
        error.value = e?.message ?? 'Failed to load tenant config';
        tenant.value = null;
      }
    } finally {
      apiLoading.value = false;
    }
  }

  function clearTenant() {
    tenant.value = null;
    error.value = null;
  }

  return {
    // state
    apiLoading,
    tenant,
    error,

    // getters
    isLoaded,
    isNhsJurisdiction,
    actions,
    actionResponseTime,
    informationBanner,
    openActions,
    actionDetails,
    actionOpeningAvailability,
    pausedActions,
    closedActions,
    inactiveActions,
    isNhsLoginEnabled,

    // actions
    fetchTenant,
    fetchRequestFormStatus,
    clearTenant,
    setActionResponseTime,
    setInformationBanner,
    clearInformationBanner,
  };
});
